import { consumerRequest } from '../../_common/core/http_services'

export class PasskeyRequestError extends Error {
  constructor(message, status) {
    super(message)
    this.name = 'PasskeyRequestError'
    this.status = status
  }
}

export function bufferToBase64url(buffer) {
  const bytes =
    buffer instanceof ArrayBuffer
      ? new Uint8Array(buffer)
      : new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength)
  let binary = ''
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index])
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

export function base64urlToBuffer(base64url) {
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  while (base64.length % 4) {
    base64 += '='
  }
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }
  return bytes.buffer
}

export function normalizeId(id) {
  if (typeof id === 'string') return base64urlToBuffer(id)
  if (id instanceof ArrayBuffer) return id
  return new Uint8Array(id.buffer, id.byteOffset, id.byteLength).slice().buffer
}

export function prepareCreateOptions(serverOptions) {
  const { publicKey } = serverOptions
  return {
    ...serverOptions,
    publicKey: {
      ...publicKey,
      challenge: normalizeId(publicKey.challenge),
      user: {
        ...publicKey.user,
        id: normalizeId(publicKey.user.id),
      },
      excludeCredentials: publicKey.excludeCredentials
        ? publicKey.excludeCredentials.map(credential => ({
            ...credential,
            id: normalizeId(credential.id),
          }))
        : undefined,
    },
  }
}

export function prepareGetOptions(serverOptions) {
  const { publicKey } = serverOptions
  return {
    ...serverOptions,
    publicKey: {
      ...publicKey,
      challenge: normalizeId(publicKey.challenge),
      allowCredentials: publicKey.allowCredentials
        ? publicKey.allowCredentials.map(credential => ({
            ...credential,
            id: normalizeId(credential.id),
          }))
        : undefined,
    },
  }
}

export async function passkeyRequest(path, method = 'get', data) {
  try {
    const response = await consumerRequest.request({
      url: path,
      method,
      data,
      headers: { 'Content-Type': 'application/json' },
    })
    return response.data
  } catch (caughtError) {
    const payload = caughtError.response && caughtError.response.data
    const message = (payload && (payload.message || payload.error)) || caughtError.message
    throw new PasskeyRequestError(message || '', caughtError.response && caughtError.response.status)
  }
}

export function isNativePasskeyRegistrationSupported() {
  return (
    typeof window !== 'undefined' &&
    !!window.PublicKeyCredential &&
    typeof navigator !== 'undefined' &&
    typeof navigator.credentials !== 'undefined' &&
    typeof navigator.credentials.create === 'function'
  )
}

export function isPasskeyRegistrationSupported() {
  return (
    typeof window !== 'undefined' &&
    ((window.desktop && window.desktop.isDesktop && window.desktop.openPasskeyEnrollment) ||
      isNativePasskeyRegistrationSupported())
  )
}

export function isPasskeyRpIdValidForOrigin(rpId, hostname) {
  const id = String(rpId || '')
    .trim()
    .toLowerCase()
  const host = String(hostname || '')
    .trim()
    .toLowerCase()
  if (!id || !host) return false
  if (host === id) return true
  if (id.indexOf('.') === -1) return false
  return host.endsWith('.' + id)
}

export function isPasskeyUnavailableOnOrigin(error) {
  if (!error || typeof error !== 'object') return false
  const name = error.name ? String(error.name) : ''
  const message = error.message ? String(error.message) : ''
  if (/relying party ID|registrable domain suffix|well-known\/webauthn/i.test(message)) {
    return true
  }
  return name === 'SecurityError'
}

export async function registerPasskey(enrollmentToken) {
  if (!window.PublicKeyCredential || typeof navigator.credentials.create !== 'function') {
    throw new DOMException('', 'NotSupportedError')
  }

  const enrollmentBinding = enrollmentToken ? { enrollment_token: enrollmentToken } : {}
  const beginResponse = await passkeyRequest('/passkey/register/begin', 'post', enrollmentBinding)
  const challengeToken = beginResponse.data && beginResponse.data.challenge_token
  const createArgs = beginResponse.data && beginResponse.data.create_args
  if (!challengeToken || !createArgs) throw new Error()

  const credential = await navigator.credentials.create(prepareCreateOptions(createArgs))
  if (!credential) throw new Error()
  const { response } = credential

  await passkeyRequest('/passkey/register/complete', 'post', {
    ...enrollmentBinding,
    challenge_token: challengeToken,
    credential_id: bufferToBase64url(credential.rawId),
    client_data_json: bufferToBase64url(response.clientDataJSON),
    attestation_object: bufferToBase64url(response.attestationObject),
  })
}
